import React, { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { Button, Typography } from "./ui";
import StackFormLayout from "./ui/stack-form-layout";
import StackFormButtons from "./ui/stack-form-buttons";
import BankSelectionModal from "./ui/bank-selection-modal";
import { AutoStackFormData } from "../pages/Autostack";
import { useAssets } from "../hooks/useAssets";
import copyRedIcon from "../assets/icons/copy-red.svg";
import infoIcon from "../assets/icons/info.svg";
import { useAllBanks } from "../hooks/query/queries";
import { useBankReferenceStore } from "../store/use-bank-reference";

interface BankTransferSetupProps {
  onContinue: () => void;
  onStepBack: () => void;
  onCancel: () => void;
}

const BankTransferSetup: React.FC<BankTransferSetupProps> = ({
  onContinue,
  onStepBack,
  onCancel,
}) => {
  const form = useFormContext<AutoStackFormData>();
  const [isCopied, setIsCopied] = useState(false);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);

  const assetToBuy = form.watch("assetToBuy");
  const frequency = form.watch("frequency");

  const { getAssetByValue } = useAssets();
  const { generateReference } = useBankReferenceStore();
  const { data: allBanks } = useAllBanks();
  const selectedAsset = getAssetByValue(assetToBuy);

  const referenceId = useMemo(() => {
    return selectedAsset?.asset
      ? generateReference(selectedAsset.asset)
      : "ETH";
  }, [selectedAsset, generateReference]);

  const getFrequencyText = () => {
    if (frequency?.type === "day") return "Daily";
    if (frequency?.type === "week") return "Weekly";
    if (frequency?.type === "month") return "Monthly";
    return "Weekly";
  };

  const handleCopyReference = async () => {
    try {
      await navigator.clipboard.writeText(referenceId || "");
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleHowToSetup = () => {
    setIsBankModalOpen(true);
  };

  const handleCloseBankModal = () => {
    setIsBankModalOpen(false);
  };

  return (
    <StackFormLayout
      title="Bank Transfer AutoStack Setup"
      onCancel={onCancel}
      showCancelHeader={true}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between border border-gray-200 py-3 px-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <img
              src={selectedAsset?.image}
              alt={selectedAsset?.assetName}
              className="w-6 h-6 rounded-full"
            />
            <div>
              <Typography variant="h4" className="font-semibold">
                {selectedAsset?.asset.toUpperCase()}
              </Typography>
              <Typography variant="p" className="text-gray-500 text-sm">
                {selectedAsset?.assetName}
              </Typography>
            </div>
          </div>
          <Typography variant="p" className="text-gray-500">
            {getFrequencyText()}
          </Typography>
        </div>

        <div className="text-center space-y-4">
          <Typography
            variant="p"
            className="text-gray-900 text-sm font-semibold"
          >
            Use the below reference ID for your recurring transfer
          </Typography>

          <div className="flex items-center justify-center space-x-2">
            <div className="bg-gray-50 w-full rounded-lg p-4">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Typography variant="h2" className="font-bold text-red-500">
                  {referenceId}
                </Typography>
                <button
                  onClick={handleCopyReference}
                  className="text-accent-pink transition-colors"
                  aria-label="Copy reference ID"
                >
                  {isCopied ? (
                    <Check size={16} className="text-green-500" />
                  ) : (
                    <img src={copyRedIcon} alt="copy" />
                  )}
                </button>
              </div>
              <Typography variant="p" className="text-gray-500 text-sm">
                Set a recurring transfer from your
                <br />
                bank with this reference
              </Typography>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <Button
            onClick={handleHowToSetup}
            variant="outline"
            className="flex items-center space-x-2 px-6 py-3 rounded-full text-white hover:opacity-90 transition-opacity"
            style={{
              background: "linear-gradient(90deg, #5F75FF 0%, #9747FF 100%)",
            }}
          >
            <img src={infoIcon} alt="info" />
            <Typography
              variant="span"
              className="font-semibold text-white text-sm"
            >
              How to set-up recurring payments
            </Typography>
          </Button>
        </div>

        <div className="text-center">
          <Typography variant="p" className="text-gray-500">
            Once we receive a transfer with this reference,
            <br />
            we'll trigger your auto-purchase.
          </Typography>
        </div>

        <StackFormButtons
          onBack={onStepBack}
          onContinue={onContinue}
          isSubmitType={false}
        />
      </div>

      <BankSelectionModal
        banks={allBanks || []}
        isOpen={isBankModalOpen}
        onClose={handleCloseBankModal}
      />
    </StackFormLayout>
  );
};

export default BankTransferSetup;
